<template>
  <div v-title="$route.meta.title">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>考试培训管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/training/examination' }">考试管理</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <el-form
      ref="form"
      :model="form"
      :rules="formRules"
      size="medium"
      label-width="140px"
      label-position="left"
      @submit.native.prevent="onSubmit">
      <div class="exam-editor">
        <aside class="exam-sidebar">
          <section class="exam-side-panel">
            <div class="exam-side-panel__header">
              <strong>题号目录</strong>
              <span class="exam-side-panel__score">总分: {{ totalScore }}分</span>
            </div>
            <div class="exam-side-panel__sub">
              <span>及格分数</span>
              <span>{{ form.passing_score || 0 }}分 ({{ passingRate }}%)</span>
            </div>
            <draggable
              v-model="draggableQuestionList"
              tag="div"
              :class="['question-index-list', isStructureLocked ? 'is-disabled' : '']"
              :disabled="isStructureLocked"
              :options="{animation: 150}">
              <div
                v-for="(question, index) in form.question_list"
                :key="question.client_id"
                class="question-index-item">
                <el-tooltip
                  :content="question.title"
                  placement="top">
                  <button
                    type="button"
                    :class="['question-index-button', activeQuestionIndex === index ? 'is-active' : '']"
                    @click="scrollToQuestion(index)">
                    {{ index + 1 }}
                  </button>
                </el-tooltip>
                <button
                  v-if="!isStructureLocked"
                  type="button"
                  class="question-index-remove"
                  @click.stop="removeQuestion(index)">
                  ×
                </button>
              </div>
              <div v-if="!form.question_list.length"
                   class="question-index-empty">
                暂无题目
              </div>
            </draggable>
          </section>

          <section class="exam-side-panel">
            <div class="exam-side-panel__title">添加题型</div>
            <button
              type="button"
              class="question-add-button"
              :disabled="isStructureLocked"
              @click="addQuestion(questionConsts.QUESTION_TYPE_SINGLE)">
              <i class="el-icon-circle-check"></i>
              <span>单选题</span>
            </button>
            <button
              type="button"
              class="question-add-button"
              :disabled="isStructureLocked"
              @click="addQuestion(questionConsts.QUESTION_TYPE_MULTI)">
              <i class="el-icon-finished"></i>
              <span>多选题</span>
            </button>
            <button
              type="button"
              class="question-add-button"
              :disabled="isStructureLocked"
              @click="addQuestion(questionConsts.QUESTION_TYPE_FILL_BLANK)">
              <i class="el-icon-edit-outline"></i>
              <span>填空题</span>
            </button>
            <button
              type="button"
              class="question-add-button"
              :disabled="isStructureLocked"
              @click="openQuestionDialog">
              <i class="el-icon-plus"></i>
              <span>从题库选择</span>
            </button>
          </section>

          <div class="exam-side-actions">
            <el-button
              type="primary"
              native-type="submit"
              :disabled="isReadonly"
              :loading="submitting || uploading">
              保存
            </el-button>
            <el-button @click="$router.back()">取消</el-button>
          </div>
        </aside>

        <main class="exam-main">
          <layout-panel>
            <template slot="header">
              <span class="exam-panel-title">
                <span>考试信息</span>
                <el-tooltip
                  v-if="allowCopyExam"
                  content="复制考试"
                  placement="top">
                  <button
                    ref="copyExam"
                    type="button"
                    class="sj-icon-copy exam-copy-button"></button>
                </el-tooltip>
              </span>
            </template>

            <div class="exam-form-section">
              <el-form-item label="考试标题"
                            prop="title">
                <el-input
                  v-model="form.title"
                  clearable
                  :disabled="isReadonly"
                  maxlength="100"
                  placeholder="请输入考试标题"/>
              </el-form-item>

              <el-form-item label="是否通用"
                            prop="is_common">
                <el-radio-group v-model="form.is_common"
                                :disabled="isScopeLocked"
                                @change="onIsCommonChanged">
                  <el-radio
                    v-for="item in opts.isCommonMaps"
                    :key="item.value"
                    :label="item.value">
                    {{ item.label }}
                  </el-radio>
                </el-radio-group>
              </el-form-item>

              <el-form-item
                v-if="Number(form.is_common) === consts.IS_COMMON_DEDICATED"
                label="所属客户"
                prop="customerid">
                <remote-select
                  v-model="form.customerid"
                  class="width-max"
                  clearable
                  :disabled="isScopeLocked"
                  placeholder="请选择客户"
                  :query-request="getBasicCustomerTable"
                  :find-request="getCustomerDetail"
                  @change="onCustomerChanged"/>
              </el-form-item>

              <el-form-item label="所属知识库"
                            prop="training_knowledge_id">
                <remote-select
                  v-model="form.training_knowledge_id"
                  class="width-max"
                  clearable
                  keyword-key="name"
                  label-key="name"
                  value-key="id"
                  placeholder="请选择知识库"
                  :disabled="knowledgeDisabled"
                  :query-request="queryTrainingKnowledge"
                  :find-request="getTrainingKnowledge"
                  :query-params="knowledgeQueryParams"/>
              </el-form-item>

              <el-form-item label="是否开卷"
                            prop="open_book_exam">
                <el-radio-group v-model="form.open_book_exam"
                                :disabled="isReadonly"
                                @input="onOpenBookExamChanged">
                  <el-radio
                    v-for="item in opts.openBookMaps"
                    :key="item.value"
                    :label="item.value">
                    {{ item.label }}
                  </el-radio>
                </el-radio-group>
              </el-form-item>

              <el-form-item label="单题分值"
                            prop="question_score"
                            required
                            v-if="form.open_book_exam===consts.OPEN_BOOK_EXAM_CLOSED">
                <div class="inline-number-field">
                  <el-input-number
                    v-model="form.question_score"
                    :min="1"
                    :precision="0"
                    :disabled="isQuestionScoreLocked"
                    :controls="false"
                    @change="validatePassingScore"/>
                  <span>分/题</span>
                  <span class="field-tip">({{ questionCount }}题 × {{ form.question_score || 0 }}分 = {{
                      totalScore
                    }}分)</span>
                </div>
              </el-form-item>

              <el-form-item label="及格分数"
                            prop="passing_score"
                            required
                            v-if="form.open_book_exam===consts.OPEN_BOOK_EXAM_CLOSED">
                <div class="inline-number-field">
                  <el-input-number
                    v-model="form.passing_score"
                    :min="0"
                    :precision="0"
                    :disabled="isReadonly"
                    :controls="false"/>
                  <span>分</span>
                  <span class="field-tip">总分: {{ totalScore }}分</span>
                </div>
              </el-form-item>

              <el-form-item label="考试时效"
                            prop="exam_duration"
                            required>
                <div class="inline-number-field">
                  <el-input-number
                    v-model="form.exam_duration"
                    :min="1"
                    :precision="0"
                    :disabled="isReadonly"
                    :controls="false"/>
                  <span>分钟</span>
                </div>
              </el-form-item>

              <el-form-item label="题目顺序"
                            prop="question_sort_type">
                <el-radio-group v-model="form.question_sort_type"
                                :disabled="isReadonly">
                  <el-radio
                    v-for="item in opts.sortTypeMaps"
                    :key="item.value"
                    :label="item.value">
                    {{ item.label }}
                  </el-radio>
                </el-radio-group>
              </el-form-item>

              <el-form-item label="选项顺序"
                            prop="question_options_sort_type">
                <el-radio-group v-model="form.question_options_sort_type"
                                :disabled="isReadonly">
                  <el-radio
                    v-for="item in opts.sortTypeMaps"
                    :key="item.value"
                    :label="item.value">
                    {{ item.label }}
                  </el-radio>
                </el-radio-group>
              </el-form-item>
            </div>
          </layout-panel>

          <layout-panel>
            <template slot="header">题目列表 ({{ questionCount }}题)</template>
            <div v-if="!form.question_list.length"
                 class="question-empty">
              请从左侧添加题目
            </div>
            <div
              v-for="(question, index) in form.question_list"
              :key="question.client_id"
              :ref="`questionCard_${index}`"
              :class="['exam-question-card', activeQuestionIndex === index ? 'is-active' : '']"
              @click="activeQuestionIndex = index">
              <div class="exam-question-card__header">
                <strong>{{ index + 1 }}. {{ getQuestionTypeLabel(question.question_type) }}</strong>
                <el-button
                  v-if="!isStructureLocked"
                  type="text"
                  icon="el-icon-delete"
                  @click.stop="removeQuestion(index)">
                </el-button>
              </div>
              <question-form
                :form="question"
                :question-type="question.question_type"
                :prop-prefix="`question_list.${index}`"
                :is-view-mode="isReadonly"
                :is-answer-locked="isAnswerLocked"
                :is-option-structure-locked="isOptionStructureLocked"
                @validate-field="validateField"
                @uploading-change="uploading = $event"/>
            </div>
          </layout-panel>
        </main>
      </div>
    </el-form>

    <el-dialog
      title="从题库选择题目"
      width="72%"
      class="question-bank-dialog"
      :visible.sync="questionDialogVisible"
      @open="onQuestionDialogOpen">
      <div class="question-bank-toolbar">
        <el-input
          v-model="questionSearch.title"
          clearable
          prefix-icon="el-icon-search"
          placeholder="搜索题目"
          @keyup.enter.native="searchQuestionBank"/>
        <remote-select
          v-model="questionSearch.customerid"
          clearable
          placeholder="所有客户"
          :query-request="getBasicCustomerTable"
          :find-request="getCustomerDetail"/>
        <remote-select
          v-model="questionSearch.training_knowledge_id"
          clearable
          keyword-key="name"
          label-key="name"
          value-key="id"
          placeholder="所有知识库"
          :query-request="queryTrainingKnowledge"
          :find-request="getTrainingKnowledge"/>
        <el-select
          v-model="questionSearch.question_type"
          clearable
          placeholder="所有类型">
          <el-option
            v-for="item in questionOpts.questionTypeMaps"
            :key="item.value"
            :label="item.label"
            :value="item.value"/>
        </el-select>
        <el-button type="primary"
                   @click="searchQuestionBank">搜索
        </el-button>
        <el-button @click="resetQuestionSearch">重置</el-button>
      </div>

      <div v-loading="questionDialogLoading"
           class="question-bank-list">
        <div
          v-for="row in questionBankList"
          :key="row.id"
          class="question-bank-item">
          <el-checkbox
            :value="isBankQuestionSelected(row)"
            @change="checked => toggleBankQuestion(row, checked)">
          </el-checkbox>
          <div class="question-bank-item__body">
            <div class="question-bank-item__meta">
              <span class="question-tag">{{ getQuestionTypeLabel(getRowQuestionType(row)) }}</span>
              <span>Q{{ row.id }}</span>
              <span v-if="formatAccuracy(row)"
                    class="accuracy-tag">正确率: {{ formatAccuracy(row) }}</span>
            </div>
            <div class="question-bank-item__title">{{ getRowQuestionTitle(row) }}</div>
            <div
              v-for="(option, index) in getRowQuestionOptions(row)"
              :key="option.uuid || index"
              class="question-bank-item__option">
              {{ getOptionLetter(index) }}. {{ option.title }}
            </div>
          </div>
        </div>
        <div v-if="!questionBankList.length && !questionDialogLoading"
             class="question-bank-empty">
          暂无题目
        </div>
      </div>

      <div class="question-bank-pagination">
        <span>共 {{ questionPagination.total }} 条</span>
        <el-pagination
          background
          layout="prev, pager, next, jumper"
          :current-page="questionPagination.current_page"
          :page-size="questionPagination.per_page"
          :total="questionPagination.total"
          @current-change="handleQuestionPageChange"/>
      </div>

      <template slot="footer">
        <div class="question-bank-footer">
          <span>已选 {{ selectedQuestionRows.length }} 题</span>
          <div>
            <el-button @click="questionDialogVisible = false">取消</el-button>
            <el-button
              type="primary"
              :disabled="!selectedQuestionRows.length"
              @click="addSelectedQuestions">
              添加至考试
            </el-button>
          </div>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import ClipboardJS from 'clipboard'
import QuestionForm from '@views/training/components/QuestionForm.vue'
import {
  buildTrainingExamCopyData,
  consts,
  createExamQuestion,
  getExamQuestionDuplicateKey,
  getExamSubmitPayload,
  isExamAnswerLocked,
  isExamOptionStructureLocked,
  isExamReadonly,
  isExamScopeLocked,
  isExamScoreLocked,
  isExamStructureLocked,
  normalizeTrainingExamCopyData,
  normalizeExamQuestionFromRow,
  opts,
} from '@views/training/training-exam.constants'
import { buildCourseKnowledgeQueryParams } from '@views/training/training-course.constants'
import {
  consts as questionConsts,
  getOptionLetter,
  getQuestionTypeLabel,
  isChoiceQuestion,
  isNumericAnswer,
  opts as questionOpts,
} from '@views/training/training-question.constants'

export default {
  name: 'TrainingExamForm',
  components: {
    draggable,
    QuestionForm,
  },
  data () {
    return {
      id: 0,
      consts,
      opts,
      questionConsts,
      questionOpts,
      submitting: false,
      uploading: false,
      copyExamClipboard: null,
      activeQuestionIndex: 0,
      questionDialogVisible: false,
      questionDialogLoading: false,
      questionBankList: [],
      selectedQuestionRows: [],
      questionPagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
      },
      questionSearch: {
        title: '',
        customerid: null,
        training_knowledge_id: null,
        question_type: null,
      },
      form: {
        status: consts.STATUS_NEW,
        title: '',
        target_usertype: consts.TARGETUSER_TYPE_USER,
        is_common: consts.IS_COMMON_DEDICATED,
        customerid: null,
        training_knowledge_id: null,
        open_book_exam: consts.OPEN_BOOK_EXAM_CLOSED,
        question_score: 10,
        passing_score: 60,
        exam_duration: 60,
        question_sort_type: consts.SORT_TYPE_FIXED,
        question_options_sort_type: consts.SORT_TYPE_FIXED,
        question_list: [],
      },
    }
  },
  computed: {
    formRules () {
      return {
        title: [
          {required: true, message: '请输入考试标题', trigger: 'blur'},
          {max: 100, message: '考试标题最多 100 个字符', trigger: 'blur'},
        ],
        target_usertype: [{required: true, message: '请选择用户类型', trigger: 'change'}],
        is_common: [{required: true, message: '请选择是否通用', trigger: 'change'}],
        customerid: [{validator: this.validateCustomer, trigger: 'change'}],
        training_knowledge_id: [{required: true, message: '请选择所属知识库', trigger: 'change'}],
        open_book_exam: [{required: true, message: '请选择是否开卷', trigger: 'change'}],
        question_score: [{validator: this.validatePositiveNumber('请输入单题分值'), trigger: 'blur'}],
        passing_score: [{validator: this.validatePassingScoreRule, trigger: 'blur'}],
        exam_duration: [{validator: this.validatePositiveNumber('请输入考试时效'), trigger: 'blur'}],
        question_sort_type: [{required: true, message: '请选择题目顺序', trigger: 'change'}],
        question_options_sort_type: [{required: true, message: '请选择选项顺序', trigger: 'change'}],
      }
    },
    knowledgeQueryParams () {
      return buildCourseKnowledgeQueryParams(this.form)
    },
    knowledgeDisabled () {
      return this.isScopeLocked || (Number(this.form.is_common) === consts.IS_COMMON_DEDICATED && !this.form.customerid)
    },
    isReadonly () {
      return isExamReadonly(this.form.status)
    },
    isScopeLocked () {
      return isExamScopeLocked(this.form.status)
    },
    isStructureLocked () {
      return isExamStructureLocked(this.form.status)
    },
    isQuestionScoreLocked () {
      return isExamScoreLocked(this.form.status)
    },
    isAnswerLocked () {
      return isExamAnswerLocked(this.form.status)
    },
    isOptionStructureLocked () {
      return isExamOptionStructureLocked(this.form.status)
    },
    allowCopyExam () {
      return !!this.id
    },
    allowPasteExam () {
      return !this.id
    },
    draggableQuestionList: {
      get () {
        return this.form.question_list
      },
      set (list) {
        console.log(list)
        const activeQuestion = this.form.question_list[this.activeQuestionIndex]
        this.form.question_list = list
        const activeIndex = activeQuestion
          ? this.form.question_list.findIndex(question => question.client_id === activeQuestion.client_id)
          : -1
        this.activeQuestionIndex = activeIndex >= 0 ? activeIndex : 0
      },
    },
    questionCount () {
      return this.form.question_list.length
    },
    totalScore () {
      return this.questionCount * (Number(this.form.question_score) || 0)
    },
    passingRate () {
      if (!this.totalScore) {
        return 0
      }
      return Math.round((Number(this.form.passing_score) || 0) / this.totalScore * 100)
    },
  },
  created () {
    if (this.$route.params.id) {
      this.id = Number(this.$route.params.id)
      this.loadDetail(this.id)
    } else {
      this.applyRoutePrefill()
    }
  },
  mounted () {
    if (this.allowCopyExam) {
      this.bindCopyExam()
    }
    if (this.allowPasteExam) {
      document.addEventListener('paste', this.pasteExam)
    }
  },
  destroyed () {
    if (this.copyExamClipboard) {
      this.copyExamClipboard.destroy()
      this.copyExamClipboard = null
    }
    document.removeEventListener('paste', this.pasteExam)
  },
  methods: {
    ...request.map([
      'getBasicCustomerTable',
      'getCustomerDetail',
      'queryTrainingKnowledge',
      'getTrainingKnowledge',
      'queryTrainingQuestion',
      'getTrainingExamination',
      'createTrainingExamination',
      'updateTrainingExamination',
    ]),
    validateCustomer (rule, value, callback) {
      if (Number(this.form.is_common) === consts.IS_COMMON_DEDICATED && !value) {
        callback(new Error('请选择所属客户'))
        return
      }
      callback()
    },
    validatePositiveNumber (message) {
      return (rule, value, callback) => {
        if (Number(value) <= 0) {
          callback(new Error(message))
          return
        }
        callback()
      }
    },
    validatePassingScoreRule (rule, value, callback) {
      if (Number(value) <= 0 && this.form.open_book_exam === consts.OPEN_BOOK_EXAM_CLOSED) {
        callback(new Error('请输入及格分数'))
        return
      }
      if (this.totalScore && Number(value) > this.totalScore && this.form.open_book_exam === consts.OPEN_BOOK_EXAM_CLOSED) {
        callback(new Error('及格分数不能超过总分'))
        return
      }
      if (Number(value) >= 100 && this.form.open_book_exam === consts.OPEN_BOOK_EXAM_CLOSED) {
        callback(new Error('及格分数不能大于等于100分'))
        return
      }
      callback()
    },
    validatePassingScore () {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.validateField('passing_score')
      })
    },
    validateField (field) {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.validateField(field)
      })
    },
    bindCopyExam () {
      this.copyExamClipboard = new ClipboardJS(this.$refs.copyExam, {
        text: () => JSON.stringify(buildTrainingExamCopyData(this.form)),
      })
      this.copyExamClipboard.on('success', () => {
        this.$message.success('复制成功')
      })
      this.copyExamClipboard.on('error', () => {
        this.$message.error('复制失败')
      })
    },
    pasteExam (event) {
      if (!this.allowPasteExam) {
        return
      }
      const paste = (event.clipboardData || window.clipboardData).getData('text')
      let copyData = null
      try {
        copyData = normalizeTrainingExamCopyData(JSON.parse(paste))
      } catch (e) {}
      if (!copyData) {
        return
      }

      event.stopPropagation()
      event.preventDefault()
      const title = copyData.title || '未命名考试'
      this.$confirm(`发现[${title}]考试JSON，是否立即写入当前考试？`, '提示', {type: 'warning'}).then(() => {
        this.form = Object.assign({}, this.form, copyData, {
          status: consts.STATUS_NEW,
        })
        this.activeQuestionIndex = 0
        this.clearScopeValidate()
        this.validatePassingScore()
        this.$message.success('考试JSON写入成功')
      })
    },
    applyRoutePrefill () {
      const query = this.$route.query
      if (query.target_usertype) {
        this.form.target_usertype = Number(query.target_usertype)
      }
      if (query.is_common !== undefined && query.is_common !== '') {
        this.form.is_common = Number(query.is_common)
      }
      if (query.customerid) {
        this.form.customerid = String(query.customerid)
      }
      if (query.training_knowledge_id) {
        this.form.training_knowledge_id = Number(query.training_knowledge_id)
      }
    },
    onOpenBookExamChanged (value) {
      if (value === consts.OPEN_BOOK_EXAM_OPEN) {
        this.$nextTick(() => {
          this.$refs.form && this.$refs.form.clearValidate(['question_score', 'passing_score'])
        })
      }
    },
    loadDetail (id) {
      request.common('getTrainingExamination', {id}).then(({data}) => {
        this.form = Object.assign({}, this.form, {
          title: data.title || '',
          status: Number(data.status) || consts.STATUS_NEW,
          target_usertype: data.target_usertype || consts.TARGETUSER_TYPE_USER,
          is_common: Number(data.is_common || consts.IS_COMMON_DEDICATED),
          customerid: Number(data.customerid) > 0 ? String(data.customerid) : null,
          training_knowledge_id: data.training_knowledge_id || null,
          open_book_exam: Number(data.open_book_exam),
          question_score: Number(data.question_score) || 10,
          passing_score: Number(data.passing_score) || 60,
          exam_duration: Number(data.exam_duration) || 60,
          question_sort_type: Number(data.question_sort_type) || consts.SORT_TYPE_FIXED,
          question_options_sort_type: Number(data.question_options_sort_type) || consts.SORT_TYPE_FIXED,
          question_list: Array.isArray(data.question_list)
            ? data.question_list.map(item => normalizeExamQuestionFromRow(item))
            : [],
        })
      })
    },
    onIsCommonChanged (value) {
      if (this.isScopeLocked) {
        return
      }
      if (Number(value) === consts.IS_COMMON_SHARED) {
        this.form.customerid = null
      }
      this.form.training_knowledge_id = null
      this.clearScopeValidate()
    },
    onCustomerChanged () {
      if (this.isScopeLocked) {
        return
      }
      this.form.training_knowledge_id = null
      this.clearScopeValidate()
    },
    clearScopeValidate () {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate(['customerid', 'training_knowledge_id'])
      })
    },
    addQuestion (type) {
      if (this.isStructureLocked) {
        return
      }
      this.form.question_list.push(createExamQuestion(type))
      this.activeQuestionIndex = this.form.question_list.length - 1
      // 每新增一条要检测一下分数是否超限
      this.validatePassingScore()
      this.$nextTick(() => {
        this.scrollToQuestion(this.activeQuestionIndex)
      })
    },
    removeQuestion (index) {
      if (this.isStructureLocked) {
        return
      }
      this.$confirm(`确定删除第 ${index + 1} 题吗？`, '提示', {type: 'warning'}).then(() => {
        this.form.question_list.splice(index, 1)
        if (this.activeQuestionIndex >= this.form.question_list.length) {
          this.activeQuestionIndex = Math.max(this.form.question_list.length - 1, 0)
        }
        this.validatePassingScore()
      })
    },
    scrollToQuestion (index) {
      this.activeQuestionIndex = index
      this.$nextTick(() => {
        const ref = this.$refs[`questionCard_${index}`]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.scrollIntoView) {
          el.scrollIntoView({behavior: 'smooth', block: 'start'})
        }
      })
    },
    openQuestionDialog () {
      if (this.isStructureLocked) {
        return
      }
      this.questionDialogVisible = true
    },
    onQuestionDialogOpen () {
      this.selectedQuestionRows = []
      this.loadQuestionBank()
    },
    getQuestionBankParams () {
      const params = {
        current_page: this.questionPagination.current_page,
        per_page: this.questionPagination.per_page,
        title: this.questionSearch.title,
        target_usertype: this.form.target_usertype,
      }
      if (this.questionSearch.customerid) {
        params.customerid = [this.questionSearch.customerid]
      }
      if (this.questionSearch.training_knowledge_id) {
        params.training_knowledge_id = [this.questionSearch.training_knowledge_id]
      }
      if (this.questionSearch.question_type) {
        params.question_type = [this.questionSearch.question_type]
      }
      return params
    },
    loadQuestionBank () {
      this.questionDialogLoading = true
      request.page('queryTrainingQuestion', this.getQuestionBankParams()).then(({data}) => {
        this.questionBankList = data.data || []
        this.questionPagination.current_page = Number(data.current_page) || 1
        this.questionPagination.per_page = Number(data.per_page) || 10
        this.questionPagination.total = Number(data.total) || 0
      }).finally(() => {
        this.questionDialogLoading = false
      })
    },
    searchQuestionBank () {
      this.questionPagination.current_page = 1
      this.loadQuestionBank()
    },
    resetQuestionSearch () {
      this.questionSearch = {
        title: '',
        customerid: null,
        training_knowledge_id: null,
        question_type: null,
      }
      this.searchQuestionBank()
    },
    handleQuestionPageChange (page) {
      this.questionPagination.current_page = page
      this.loadQuestionBank()
    },
    getBankQuestionKey (row) {
      return Number(row.id)
    },
    isBankQuestionSelected (row) {
      const key = this.getBankQuestionKey(row)
      return this.selectedQuestionRows.some(item => this.getBankQuestionKey(item) === key)
    },
    toggleBankQuestion (row, checked) {
      const key = this.getBankQuestionKey(row)
      if (checked && !this.isBankQuestionSelected(row)) {
        this.selectedQuestionRows.push(row)
      }
      if (!checked) {
        this.selectedQuestionRows = this.selectedQuestionRows.filter(item => this.getBankQuestionKey(item) !== key)
      }
    },
    addSelectedQuestions () {
      if (!this.selectedQuestionRows.length) {
        this.$message.error('请选择题目')
        return
      }
      this.selectedQuestionRows.forEach(row => {
        this.form.question_list.push(normalizeExamQuestionFromRow(row))
      })
      this.activeQuestionIndex = this.form.question_list.length - 1
      this.$message.success(`已添加 ${this.selectedQuestionRows.length} 题`)
      this.selectedQuestionRows = []
      this.questionDialogVisible = false
      this.validatePassingScore()
    },
    getRowQuestionJson (row) {
      return row.question_json || {}
    },
    getRowQuestionType (row) {
      return row.question_type || row.type || this.getRowQuestionJson(row).question_type
    },
    getRowQuestionTitle (row) {
      return row.title || this.getRowQuestionJson(row).title || '-'
    },
    getRowQuestionOptions (row) {
      const json = this.getRowQuestionJson(row)
      if (Array.isArray(json.options)) {
        return json.options
      }
      return Array.isArray(row.options) ? row.options : []
    },
    getFirstValue (row, keys, defaultValue = null) {
      const key = keys.find(item => row[item] !== undefined && row[item] !== null)
      return key ? row[key] : defaultValue
    },
    formatAccuracy (row) {
      const direct = this.getFirstValue(row, ['correct_rate', 'accuracy_rate', 'accuracy'], null)
      if (direct === null) {
        return ''
      }
      const value = Number(direct)
      const percent = value <= 1 ? value * 100 : value
      return `${percent.toFixed(1).replace(/\.0$/, '')}%`
    },
    getQuestionUniqueKey (question) {
      return getExamQuestionDuplicateKey(question)
    },
    validateDuplicateQuestions () {
      const questionKeyMap = {}
      for (let index = 0; index < this.form.question_list.length; index += 1) {
        const key = this.getQuestionUniqueKey(this.form.question_list[index])
        if (questionKeyMap[key] !== undefined) {
          const firstIndex = questionKeyMap[key] + 1
          const duplicateIndex = index + 1
          this.activeQuestionIndex = index
          this.$message.error(`第 ${duplicateIndex} 题与第 ${firstIndex} 题重复，请修改后再提交`)
          return false
        }
        questionKeyMap[key] = index
      }
      return true
    },
    getQuestionTypeLabel,
    getOptionLetter,
    validateQuestions () {
      if (!this.form.question_list.length) {
        this.$message.error('请至少添加一道题目')
        return false
      }
      for (let index = 0; index < this.form.question_list.length; index += 1) {
        const question = this.form.question_list[index]
        const label = `第 ${index + 1} 题`

        if (this.totalScore !== 100 && this.form.open_book_exam === consts.OPEN_BOOK_EXAM_CLOSED) {
          this.$message.error('闭卷类型的试卷总分必须是100')
          return false
        }
        if (!question.title) {
          this.activeQuestionIndex = index
          this.$message.error(`${label}请填写题目标题`)
          return false
        }
        if (isChoiceQuestion(question.question_type)) {
          if (!Array.isArray(question.options) || !question.options.length) {
            this.activeQuestionIndex = index
            this.$message.error(`${label}请添加答案选项`)
            return false
          }
          if (!question.options.every(option => option.title)) {
            this.activeQuestionIndex = index
            this.$message.error(`${label}请填写完整选项内容`)
            return false
          }
          if (Number(question.question_type) === questionConsts.QUESTION_TYPE_SINGLE && !question.standard_answer) {
            this.activeQuestionIndex = index
            this.$message.error(`${label}请选择正确答案`)
            return false
          }
          if (Number(question.question_type) === questionConsts.QUESTION_TYPE_MULTI &&
            (!Array.isArray(question.standard_answer) || !question.standard_answer.length)) {
            this.activeQuestionIndex = index
            this.$message.error(`${label}请选择正确答案`)
            return false
          }
          if (Number(question.question_type) === questionConsts.QUESTION_TYPE_MULTI &&
            (Array.isArray(question.standard_answer) && question.standard_answer.length <= 1)) {
            this.activeQuestionIndex = index
            this.$message.error(`${label}请选择至少2个答案选项`)
            return false
          }
        } else if (!isNumericAnswer(question.standard_answer)) {
          this.activeQuestionIndex = index
          this.$message.error(`${label}填空题答案仅支持数字`)
          return false
        }
      }
      // if (!this.validateDuplicateQuestions()) {
      //   return false
      // }
      return true
    },
    onSubmit () {
      if (this.submitting || this.uploading || this.isReadonly) {
        return
      }
      this.$refs.form.validate(valid => {
        if (!valid) {
          this.$message.error('请检查表单填写是否正确')
          return
        }
        if (!this.validateQuestions()) {
          return
        }
        const requestName = this.id ? 'updateTrainingExamination' : 'createTrainingExamination'
        const params = this.id
          ? {id: this.id, ...getExamSubmitPayload(this.form)}
          : getExamSubmitPayload(this.form)
        this.submitting = true
        request.common(requestName, params).then(() => {
          this.$message.success(this.id ? '保存成功' : '创建成功')
          this.$router.push({ path: '/training/examination' })
        }).finally(() => {
          this.submitting = false
        })
      })
    },
  },
}
</script>

<style scoped
       lang="scss">
.exam-editor {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.exam-sidebar {
  position: sticky;
  top: 12px;
}

.exam-side-panel {
  margin-bottom: 12px;
  padding: 14px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(31, 45, 61, 0.06);
}

.exam-side-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  color: #1f2d3d;
}

.exam-side-panel__score {
  color: #2468f2;
  font-size: 12px;
}

.exam-side-panel__sub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #606266;
  font-size: 12px;
  line-height: 20px;
}

.exam-side-panel__title {
  margin-bottom: 12px;
  color: #1f2d3d;
  font-weight: 600;
}

.question-index-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.question-index-list.is-disabled .question-index-button {
  cursor: default;
}

.question-index-item {
  position: relative;
}

.question-index-button {
  min-width: 40px;
  height: 30px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: #606266;
  background: #fff;
  cursor: pointer;
}

.question-index-remove {
  display: none;
  position: absolute;
  top: -6px;
  right: -6px;
  width: 14px;
  height: 14px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: #f56c6c;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  cursor: pointer;
}

.question-index-item:hover .question-index-remove {
  display: block;
}

.question-index-button.is-active {
  border-color: #2468f2;
  color: #fff;
  background: #2468f2;
}

.question-index-empty {
  color: #909399;
  font-size: 12px;
}

.question-add-button {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 34px;
  border: 0;
  color: #303133;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.question-add-button:hover {
  color: #2468f2;
}

.exam-side-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
}

.exam-side-actions .el-button {
  width: 100%;
  margin-left: 0;
}

.exam-main {
  min-width: 0;
}

.exam-panel-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.exam-copy-button {
  border: 0;
  color: #1e88e5;
  background: transparent;
  cursor: pointer;
}

.exam-form-section {
  max-width: 980px;
}

.inline-number-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

.inline-number-field .el-input-number {
  width: 130px;
}

.field-tip {
  color: #8492a6;
  font-size: 12px;
}

.question-empty {
  padding: 28px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  color: #909399;
  text-align: center;
}

.exam-question-card {
  padding: 18px 20px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #f8fbff;
}

.exam-question-card + .exam-question-card {
  margin-top: 14px;
}

.exam-question-card.is-active {
  border-color: #2468f2;
  box-shadow: 0 0 0 1px #2468f2 inset;
}

.exam-question-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  color: #1f2d3d;
}

.question-bank-toolbar {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 150px 170px 130px auto auto;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.question-bank-list {
  min-height: 360px;
  max-height: 520px;
  overflow: auto;
  padding: 14px 0;
}

.question-bank-item {
  display: flex;
  gap: 12px;
  padding: 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
}

.question-bank-item + .question-bank-item {
  margin-top: 10px;
}

.question-bank-item__body {
  flex: 1;
  min-width: 0;
}

.question-bank-item__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  color: #606266;
  font-size: 12px;
}

.question-bank-item__title {
  margin-bottom: 6px;
  color: #1f2d3d;
  font-weight: 600;
  line-height: 20px;
}

.question-bank-item__option {
  color: #303133;
  font-size: 12px;
  line-height: 20px;
}

.question-bank-empty {
  padding: 48px 0;
  color: #909399;
  text-align: center;
}

.question-bank-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

.question-bank-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.question-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 22px;
  padding: 0 8px;
  border-radius: 3px;
  color: #606266;
  background: #f4f5f7;
  box-sizing: border-box;
}

.accuracy-tag {
  padding: 2px 6px;
  border-radius: 3px;
  color: #f56c6c;
  background: #fef0f0;
}

@media (max-width: 1200px) {
  .exam-editor {
    grid-template-columns: 1fr;
  }

  .exam-sidebar {
    position: static;
  }

  .question-bank-toolbar {
    grid-template-columns: 1fr 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/exam/TrainingExamForm.vue